import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, LogOut, Menu, Search, Users, Home, Store, PlaySquare, Zap, Ticket, Megaphone } from "lucide-react";
import LogoCamara from "../assets/LogoCamara.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { FaUsers } from "react-icons/fa";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, isAdmin, handleLogout } = useAuth();
  const { loginWithRedirect, isAuthenticated: auth0IsAuthenticated, user: auth0User, isLoading: auth0Loading } = useAuth0();
  const desktopMenuRef = useRef<HTMLDivElement | null>(null);

  const [storedUserState, setStoredUserState] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const nextUser = user || auth0User || null;
    if (nextUser) {
      setStoredUserState(nextUser);
      localStorage.setItem("user", JSON.stringify(nextUser));
      localStorage.setItem("isAuthenticated", "true");
      return;
    }

    const persisted = localStorage.getItem("user");
    if (persisted) {
      try {
        setStoredUserState(JSON.parse(persisted));
      } catch {
        setStoredUserState(null);
      }
    }
  }, [user, auth0User]);

  const currentUser = useMemo(() => user || auth0User || storedUserState || null, [user, auth0User, storedUserState]);
  const loggedIn = Boolean(currentUser) || isAuthenticated || auth0IsAuthenticated || localStorage.getItem("isAuthenticated") === "true";

  const handleSocio = () => {
    localStorage.removeItem("socioSeleccionado");
    navigate("/seleccionar-socio");
  };

  const navItemClass = (isActive: boolean) =>
    `cursor-pointer rounded-md px-2 py-1.5 text-sm font-medium transition ${
      isActive
        ? "bg-blue-50 text-blue-700"
        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
    }`;

  const handleLogin = async () => {
    try {
      await loginWithRedirect({
        appState: { returnTo: `${location.pathname}${location.search}` },
        authorizationParams: { redirect_uri: window.location.origin },
      });
    } catch (error) {
      console.error("Error al iniciar login con Auth0", error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!desktopMenuRef.current) return;
      if (!desktopMenuRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isOpen]);

  return (
    <nav className={`relative z-[80] shadow-md ${isAdmin ? "bg-blue-300" : "bg-white"}`}>
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <button type="button" onClick={() => navigate("/institucional")} className="flex items-center" title="Ir a Institucional">
              <img src={LogoCamara} alt="Camara Comercial Bolivar" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" />
              <div className="ml-2 hidden md:flex flex-col items-start text-left leading-tight">
                <span className={`text-sm sm:text-base lg:text-xl font-bold ${isAdmin ? "text-white" : "text-gray-800"}`}>
                  Cámara Comercial e Industrial de Bolívar
                </span>
                {loggedIn && isAdmin && (
                  <span className="mt-1 inline-flex w-fit rounded-md bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">Administrador</span>
                )}
              </div>
            </button>
          </div>

          <div className="hidden md:flex items-center gap-3 relative z-[90]">
            {auth0Loading ? (
              <span className="text-sm text-slate-500">Cargando...</span>
            ) : loggedIn ? (
              <>
                <a
                  onClick={() => navigate("/inicio")}
                  aria-label="Ir al inicio"
                  title="Inicio"
                  className={navItemClass(location.pathname.startsWith("/inicio"))}
                >
                  <Home className="h-4 w-4" />
                </a>
                <a
                  onClick={() => navigate("/cuotas")}
                  className={navItemClass(location.pathname.startsWith("/cuotas"))}
                >
                  Cuotas
                </a>
                <a
                  onClick={() => navigate("/movimientos")}
                  className={navItemClass(location.pathname.startsWith("/movimientos"))}
                >
                  Movimientos
                </a>
                <a
                  onClick={() => navigate("/servicios")}
                  className={navItemClass(location.pathname.startsWith("/servicios"))}
                >
                  Servicios
                </a>
                <a
                  onClick={() => navigate("/campanias")}
                  className={navItemClass(location.pathname.startsWith("/campanias"))}
                >
                  Campañas
                </a>
                {isAdmin && (
                  <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center rounded-md border border-slate-200 bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    Panel
                  </button>
                )}

                <div ref={desktopMenuRef} className="relative">
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-md transition-colors"
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                  >
                    <img className="w-8 h-8 rounded-full" src={currentUser?.picture} alt={currentUser?.name} />
                    <span className={`text-base font-medium ${isAdmin ? "text-white" : "text-gray-900"}`}>{currentUser?.name || "Usuario"}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""} ${isAdmin ? "text-white" : "text-gray-800"}`} />
                  </button>

                  {isOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-[120]">
                      {isAdmin ? (
                        <>
                          <button onClick={() => { setIsOpen(false); navigate("/admin"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Search className="w-4 h-4 mr-2" />Buscar otro socio</button>
                          <button onClick={() => { setIsOpen(false); navigate("/campanias-admin"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Megaphone className="w-4 h-4 mr-2" />Gestionar campañas</button>
                          <button onClick={() => { setIsOpen(false); navigate("/cupones"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><FaUsers className="w-4 h-4 mr-2" />Gestionar usuarios</button>
                          <button onClick={() => { setIsOpen(false); navigate("/mis-cupones"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Ticket className="w-4 h-4 mr-2" />Mis cupones</button>
                          <button onClick={() => { setIsOpen(false); navigate("/admin"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">Panel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setIsOpen(false); navigate("/mis-comercios"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Store className="w-4 h-4 mr-2" />Comercios</button>
                          <button onClick={() => { setIsOpen(false); navigate("/mis-cupones"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Ticket className="w-4 h-4 mr-2" />Mis cupones</button>
                          <button onClick={() => { setIsOpen(false); navigate("/camara-stream"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><PlaySquare className="w-4 h-4 mr-2" />Cámara Stream</button>
                          <button onClick={() => { setIsOpen(false); navigate("/asistencia-energia"); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Zap className="w-4 h-4 mr-2" />Asistencia Energía</button>
                          <button onClick={handleSocio} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><Users className="w-4 h-4 mr-2" />Cambiar Socio</button>
                        </>
                      )}
                      <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"><LogOut className="w-4 h-4 mr-2" />Cerrar sesión</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                Entrar
              </button>
            )}
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              <Menu className="block h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden relative z-[110]">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {auth0Loading ? (
              <span className="block px-3 py-2 text-sm text-slate-500">Cargando...</span>
            ) : loggedIn ? (
              <>
                <div className="flex items-center px-3 py-2">
                  <img className="w-8 h-8 rounded-full mr-2" src={currentUser?.picture} alt={currentUser?.name} />
                  <span className={`text-base font-medium ${isAdmin ? "text-white" : "text-gray-900"}`}>{currentUser?.name || "Usuario"}</span>
                </div>
                <button onClick={() => { setIsMobileMenuOpen(false); navigate("/inicio"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Home className="w-5 h-5 mr-2" />Ir al inicio</button>
                <button onClick={() => { setIsMobileMenuOpen(false); navigate("/cuotas"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100">Cuotas</button>
                <button onClick={() => { setIsMobileMenuOpen(false); navigate("/movimientos"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100">Movimientos</button>
                <button onClick={() => { setIsMobileMenuOpen(false); navigate("/servicios"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100">Servicios</button>
                <button onClick={() => { setIsMobileMenuOpen(false); navigate("/campanias"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Megaphone className="w-5 h-5 mr-2" />Campañas</button>
                {isAdmin && <button onClick={() => { setIsMobileMenuOpen(false); navigate("/admin"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100">Panel</button>}
                {isAdmin && <button onClick={() => { setIsMobileMenuOpen(false); navigate("/campanias-admin"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Megaphone className="w-5 h-5 mr-2" />Gestionar campañas</button>}
                {!isAdmin && (
                  <>
                    <button onClick={() => { setIsMobileMenuOpen(false); navigate("/mis-comercios"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Store className="w-5 h-5 mr-2" />Comercios</button>
                    <button onClick={() => { setIsMobileMenuOpen(false); navigate("/mis-cupones"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Ticket className="w-5 h-5 mr-2" />Mis cupones</button>
                    <button onClick={() => { setIsMobileMenuOpen(false); navigate("/camara-stream"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><PlaySquare className="w-5 h-5 mr-2" />Cámara Stream</button>
                    <button onClick={() => { setIsMobileMenuOpen(false); navigate("/asistencia-energia"); }} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><Zap className="w-5 h-5 mr-2" />Asistencia Energía</button>
                  </>
                )}
                <button onClick={handleLogout} className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"><LogOut className="w-5 h-5 mr-2" />Cerrar sesión</button>
              </>
            ) : (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogin();
                }}
                className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100"
              >
                Entrar
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
